import { makeAutoObservable, runInAction } from 'mobx';

import { NetSuitePaymentType, NetSuitePaymentTypeModel } from '../models/NetSuitePaymentType';
import { addNetSuitePaymentType, deleteNetSuitePaymentType, getNetSuitePaymentTypes, updateNetSuitePaymentType } from '../services/NetSuitePaymentTypeAPI';
import { errorStore } from './ErrorStore';

class NetSuitePaymentTypeStore {
	data: NetSuitePaymentType[] = [];
	isLoading: boolean = false;
	showModal = true;
	currentRow: NetSuitePaymentTypeModel | undefined;

	constructor() {
		makeAutoObservable(this);
	}

	async loadData() {
		if (this.data.length || this.isLoading) return;

		try {
			this.setLoading(true);
			const response = await getNetSuitePaymentTypes();
			this.setData(response || []);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async delete(netsuitePaymentType: NetSuitePaymentType) {
		try {
			this.setLoading(true);
			await deleteNetSuitePaymentType(netsuitePaymentType.id);
			this.setData(this.data.filter(v => v.id !== netsuitePaymentType.id));
			this.clearForm();
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async update(netsuitePaymentType: NetSuitePaymentType) {
		try {
			this.setLoading(true);
			await updateNetSuitePaymentType(netsuitePaymentType);

			runInAction(() => {
				this.data = this.data.map(pt =>
					pt.id === netsuitePaymentType.id ? { ...netsuitePaymentType } : pt
				);
			});

			this.removeCurrent();
		} catch (error: any) {
			this.setError(error.message);
			throw error;
		} finally {
			this.setLoading(false);
		}
	}

	getById(id: number): NetSuitePaymentType | undefined {
		return this.data.find(type => type.id === id);
	}

	canSendToNetSuite(id?: number): boolean {
		return id != null
			? this.getById(id)?.sendToNetSuite ?? false
			: false;
	}

	setLoading(loading: boolean) {
		this.isLoading = loading;
	}

	setupNew() {
		this.currentRow = new NetSuitePaymentTypeModel({
			id: 0,
			name: '',
			isActive: true,
			prologueCode: '',
			netSuiteName: '',
			customerRequired: false,
			sendToNetSuite: false
		});
		this.setShowModal(true);
	}

	async addNew(netsuitePaymentType: NetSuitePaymentType, onSuccess?: () => void) {
		this.setLoading(true);
		try {
			const addedNetSuitePaymentType = await addNetSuitePaymentType(netsuitePaymentType);
			if (addedNetSuitePaymentType) {
				this.addRow(addedNetSuitePaymentType);
				this.clearForm();
				onSuccess?.();
			}
		} catch (error: any) {
			this.setError(error.message)
		} finally {
			this.setLoading(false);
		}
	}

	setError(error: string) {
		errorStore.report(error);
	}

	setShowModal(state: boolean) {
		this.showModal = state;
	}

	addRow(netsuitePaymentType: NetSuitePaymentType) {
		this.data = [netsuitePaymentType, ...this.data];
	}

	removeCurrent() {
		this.currentRow = undefined;
	}

	clearForm() {
		this.removeCurrent();
		this.setShowModal(false);
	}

	setData(netSuitePaymentTypes: NetSuitePaymentType[]) {
		this.data = netSuitePaymentTypes;
	}
}

export const netSuitePaymentTypeStore = new NetSuitePaymentTypeStore();

